import { useEffect, useState } from 'react';

/**
 * Whether the element has been seen yet, and whether it is on screen now.
 *
 * `seen` goes true the first time any of it scrolls into view and stays true, which is what an animation that plays
 * once is started by; `on` follows the element in and out of view, which is what a loop that should stop while
 * nobody can see it is paused by. Where the browser has no `IntersectionObserver` both are simply true, so the
 * picture is shown rather than left waiting for a signal that will never come.
 */
const useInView = (ref) => {
  const supported = typeof IntersectionObserver !== 'undefined';
  const [view, setView] = useState({ seen: !supported, on: !supported });
  useEffect(() => {
    const node = ref.current;
    if (!node || !supported) return undefined;
    const watcher = new IntersectionObserver(([entry]) => {
      setView((before) => ({ seen: before.seen || entry.isIntersecting, on: entry.isIntersecting }));
    }, { threshold: 0.15 });
    watcher.observe(node);
    return () => watcher.disconnect();
  }, [ref, supported]);
  return view;
};

export default useInView;
